import { useState, useEffect } from 'react';
import { builderApi } from '../api';
import { fmtDate, DateInput } from '../utils';

const EMPTY_PURCHASE = {
  project_id: '', supplier: '', purchase_date: new Date().toISOString().slice(0,10),
  invoice_no: '', tax_pct: '', advance_payment: '', notes: '',
};
const EMPTY_ITEM = { material_id: '', material_name: '', category: '', quantity: '', unit: '', unit_price: '' };

const fmt = n => n != null ? `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '—';

export default function BuilderPurchases({ user }) {
  const isOwner = user?.role === 'role_owner';
  const [purchases, setPurchases]     = useState([]);
  const [projects, setProjects]       = useState([]);
  const [materials, setMaterials]     = useState([]);
  const [supplierNames, setSupplierNames] = useState([]);
  const [form, setForm]               = useState(EMPTY_PURCHASE);
  const [items, setItems]             = useState([{ ...EMPTY_ITEM }]);
  const [showForm, setShowForm]       = useState(false);
  const [expanded, setExpanded]       = useState(null);
  const [loading, setLoading]         = useState(true);
  const [err, setErr]                 = useState('');
  const [fe, setFe]                   = useState({});
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [uploading, setUploading]     = useState(false);

  const ErrBanner = ({ msg }) => msg ? (
    <div style={{ background: '#fff5f5', border: '1px solid #feb2b2', borderRadius: 8,
      padding: '10px 14px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ fontSize: 18 }}>⚠️</span>
      <span style={{ color: '#c53030', fontSize: 13, fontWeight: 600, flex: 1 }}>{msg}</span>
      <button onClick={() => setErr('')} style={{ background: 'none', border: 'none', color: '#c53030', cursor: 'pointer', fontSize: 16, padding: 0 }}>✕</button>
    </div>
  ) : null;
  const inv = k => fe[k] ? { borderColor: '#e53e3e', background: '#fff5f5', boxShadow: '0 0 0 3px rgba(229,62,62,.15)' } : {};

  useEffect(() => {
    Promise.all([
      builderApi.getPurchases(),
      builderApi.getProjects(),
      builderApi.getMaterials(),
      builderApi.getSupplierNames(),
    ]).then(([p, pr, m, sn]) => {
      setPurchases(p);
      setProjects(pr);
      setMaterials(m);
      setSupplierNames((sn || []).map(s => s.supplier).filter(Boolean).sort((a, b) => a.localeCompare(b)));
    }).catch(e => setErr(e.message)).finally(() => setLoading(false));
  }, []);

  const reload = () => builderApi.getPurchases().then(setPurchases).catch(e => setErr(e.message));

  const setItem = (idx, k, v) => setItems(prev => prev.map((it, i) => i === idx ? { ...it, [k]: v } : it));

  const pickMaterial = (idx, mid) => {
    const mat = materials.find(m => String(m.id) === String(mid));
    setItems(prev => prev.map((it, i) => i === idx ? {
      ...it, material_id: mid,
      material_name: mat?.name || '', category: mat?.category || '', unit: mat?.unit || '',
    } : it));
  };

  const addItem = () => setItems(prev => [...prev, { ...EMPTY_ITEM }]);
  const removeItem = idx => setItems(prev => prev.filter((_, i) => i !== idx));

  // ── Computed totals ──────────────────────────────────────────────────────────
  const itemsTotal = items.reduce((s, it) =>
    s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0);
  const taxPct     = Number(form.tax_pct) || 0;
  const taxAmount  = itemsTotal * taxPct / 100;
  const grossTotal = itemsTotal + taxAmount;
  const advPay     = Number(form.advance_payment) || 0;
  const netOutstanding = grossTotal - advPay;

  const save = async () => {
    const errors = {};
    if (!form.purchase_date) errors.purchase_date = true;
    const validItems = items.filter(it => it.material_id && Number(it.quantity) > 0);
    if (validItems.length === 0) errors.items = true;
    setFe(errors);
    if (Object.keys(errors).length > 0) {
      setErr(errors.purchase_date ? 'Purchase date is required' : 'Add at least one material with quantity > 0');
      return;
    }
    try {
      const result = await builderApi.createPurchase({
        ...form,
        tax_pct: taxPct,
        advance_payment: advPay,
        items: validItems,
      });
      if (invoiceFile && result?.id) {
        setUploading(true);
        try {
          await builderApi.uploadInvoiceImage(result.id, invoiceFile);
        } catch (e) {
          setErr('Purchase saved but invoice image upload failed: ' + e.message);
        } finally {
          setUploading(false);
        }
      }
      setShowForm(false);
      setForm(EMPTY_PURCHASE);
      setItems([{ ...EMPTY_ITEM }]);
      setInvoiceFile(null);
      setFe({});
      reload();
      builderApi.getSupplierNames().then(sn => setSupplierNames((sn || []).map(s => s.supplier).filter(Boolean).sort((a, b) => a.localeCompare(b))));
    } catch (e) { setErr(e.message); }
  };

  const del = async id => {
    if (!window.confirm('Delete this purchase? Stock will be reversed.')) return;
    try { await builderApi.deletePurchase(id); reload(); } catch (e) { setErr(e.message); }
  };

  return (
    <div className="bld-card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 18, color: 'var(--text-primary)' }}>Purchases</h2>
        <button className="btn btn-primary" onClick={() => { setShowForm(s => !s); setForm(EMPTY_PURCHASE); setItems([{ ...EMPTY_ITEM }]); }}>
          + Record Purchase
        </button>
      </div>

      <ErrBanner msg={err} />

      {showForm && (
        <div style={{ background: 'var(--bg-secondary)', borderRadius: 8, padding: 16, marginBottom: 16, border: '1px solid var(--border-color)' }}>
          {/* ── Header fields ── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
            <div>
              <label className="form-label">Purchase Date *</label>
              <DateInput className="form-input" style={inv('purchase_date')} value={form.purchase_date}
                onChange={e => { setForm(f => ({ ...f, purchase_date: e.target.value })); if (fe.purchase_date) setFe(p => ({ ...p, purchase_date: false })); }} />
              {fe.purchase_date && <div style={{ color: '#e53e3e', fontSize: 11, marginTop: 3 }}>Required</div>}
            </div>
            <div>
              <label className="form-label">Project</label>
              <select className="form-input" value={form.project_id} onChange={e => setForm(f => ({ ...f, project_id: e.target.value }))}>
                <option value="">— No specific project —</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Supplier</label>
              <input className="form-input" list="supplier-names-list" value={form.supplier}
                onChange={e => setForm(f => ({ ...f, supplier: e.target.value }))} placeholder="Supplier name" />
              <datalist id="supplier-names-list">
                {supplierNames.map(s => <option key={s} value={s} />)}
              </datalist>
            </div>
            <div>
              <label className="form-label">Invoice No.</label>
              <input className="form-input" value={form.invoice_no}
                onChange={e => setForm(f => ({ ...f, invoice_no: e.target.value }))} placeholder="INV-001" />
            </div>
            <div>
              <label className="form-label">Tax %</label>
              <input className="form-input" type="number" min="0" max="100" step="0.01"
                value={form.tax_pct} onChange={e => setForm(f => ({ ...f, tax_pct: e.target.value }))}
                placeholder="0  (e.g. 18 for 18% GST)" />
            </div>
            <div>
              <label className="form-label">Notes</label>
              <input className="form-input" value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Optional notes" />
            </div>
          </div>

          {/* ── Line items ── */}
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8, color: 'var(--text-primary)' }}>Materials Purchased</div>
          {fe.items && <div style={{ color: '#e53e3e', fontSize: 12, marginBottom: 8 }}>Add at least one material with quantity &gt; 0</div>}
          {items.map((item, idx) => (
            <div key={idx} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr auto', gap: 8, marginBottom: 8, alignItems: 'end' }}>
              <div>
                {idx === 0 && <label className="form-label">Material</label>}
                <select className="form-input" value={item.material_id}
                  onChange={e => { pickMaterial(idx, e.target.value); if (fe.items) setFe(p => ({ ...p, items: false })); }}>
                  <option value="">Select material</option>
                  {materials.map(m => <option key={m.id} value={m.id}>{m.name} ({m.unit})</option>)}
                </select>
              </div>
              <div>
                {idx === 0 && <label className="form-label">Quantity</label>}
                <input className="form-input" type="number" min="0" value={item.quantity}
                  onChange={e => setItem(idx, 'quantity', e.target.value)} placeholder="0" />
              </div>
              <div>
                {idx === 0 && <label className="form-label">Unit Price (₹)</label>}
                <input className="form-input" type="number" min="0" value={item.unit_price}
                  onChange={e => setItem(idx, 'unit_price', e.target.value)} placeholder="0.00" />
              </div>
              <div>
                {idx === 0 && <label className="form-label">Line Total</label>}
                <div className="form-input" style={{ background: 'var(--bg-primary)', cursor: 'default', fontWeight: 600 }}>
                  {item.quantity && item.unit_price
                    ? `₹${(Number(item.quantity) * Number(item.unit_price)).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
                    : '—'}
                </div>
              </div>
              <button style={{ background: 'none', border: 'none', color: '#e53e3e', cursor: 'pointer', fontSize: 18, paddingBottom: 4 }}
                onClick={() => removeItem(idx)}>×</button>
            </div>
          ))}
          <button className="btn btn-outline" style={{ fontSize: 12, padding: '4px 12px', marginBottom: 16 }} onClick={addItem}>
            + Add Row
          </button>

          {/* ── Cost summary ── */}
          <div style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: 8,
            padding: '12px 16px', marginBottom: 16, display: 'flex', gap: 0, flexDirection: 'column', maxWidth: 360 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
              <span style={{ color: '#718096' }}>Materials Total</span>
              <span style={{ fontWeight: 600 }}>₹{itemsTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
            </div>
            {taxPct > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
                <span style={{ color: '#718096' }}>Tax ({taxPct}%)</span>
                <span style={{ fontWeight: 600, color: '#d69e2e' }}>+ ₹{taxAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, fontWeight: 700,
              borderTop: '1px solid var(--border-color)', paddingTop: 6, marginBottom: 6 }}>
              <span>Gross Total</span>
              <span style={{ color: '#3182ce' }}>₹{grossTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
            </div>
            <div style={{ marginBottom: 6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, alignItems: 'center' }}>
                <span style={{ color: '#718096' }}>Payment Now (₹)</span>
                <input type="number" min="0" step="0.01" value={form.advance_payment}
                  onChange={e => setForm(f => ({ ...f, advance_payment: e.target.value }))}
                  placeholder="0.00"
                  style={{ width: 120, textAlign: 'right', border: '1px solid var(--border-color)',
                    borderRadius: 4, padding: '2px 6px', fontSize: 13, background: 'var(--bg-secondary)',
                    color: 'var(--text-primary)' }} />
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, fontWeight: 700,
              borderTop: '1px solid var(--border-color)', paddingTop: 6 }}>
              <span>Net Outstanding</span>
              <span style={{ color: netOutstanding > 0 ? '#e53e3e' : '#38a169' }}>
                ₹{netOutstanding.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </span>
            </div>
          </div>

          {/* ── Invoice image upload ── */}
          <div style={{ marginBottom: 12 }}>
            <label className="form-label">Invoice Image (optional)</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer',
                background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: 6,
                padding: '6px 12px', fontSize: 13, color: 'var(--text-primary)' }}>
                📎 {invoiceFile ? 'Change file' : 'Attach invoice'}
                <input type="file" accept="image/*,application/pdf" style={{ display: 'none' }}
                  onChange={e => setInvoiceFile(e.target.files[0] || null)} />
              </label>
              {invoiceFile && (
                <>
                  <span style={{ fontSize: 12, color: '#38a169', fontWeight: 600 }}>{invoiceFile.name}</span>
                  <button style={{ background: 'none', border: 'none', color: '#718096', cursor: 'pointer', fontSize: 16 }}
                    onClick={() => setInvoiceFile(null)}>✕</button>
                </>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button className="btn btn-primary" onClick={save} disabled={uploading}>
              {uploading ? 'Uploading…' : 'Save Purchase'}
            </button>
            <button className="btn btn-outline" onClick={() => { setShowForm(false); setInvoiceFile(null); }}>Cancel</button>
          </div>
        </div>
      )}

      {loading ? <div style={{ color: '#718096', fontSize: 13 }}>Loading…</div> : (
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table" style={{ width: '100%', tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '10%' }} />
              <col style={{ width: '17%' }} />
              <col style={{ width: '15%' }} />
              <col style={{ width: '11%' }} />
              <col style={{ width: '12%' }} />
              <col style={{ width: '12%' }} />
              <col style={{ width: '12%' }} />
              <col style={{ width: '7%' }} />
              <col style={{ width: '6%' }} />
            </colgroup>
            <thead>
              <tr>
                <th>Date</th>
                <th>Supplier</th>
                <th>Project</th>
                <th>Invoice</th>
                <th style={{ textAlign: 'right' }}>Gross</th>
                <th style={{ textAlign: 'right' }}>Advance</th>
                <th style={{ textAlign: 'right' }}>Outstanding</th>
                <th>Items</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {purchases.length === 0 && (
                <tr><td colSpan={9} style={{ textAlign: 'center', color: '#718096', padding: 24 }}>No purchases recorded yet</td></tr>
              )}
              {purchases.map(p => {
                const gross = Number(p.gross_amount) > 0 ? Number(p.gross_amount) : Number(p.total_amount);
                const adv = Number(p.advance_payment) || 0;
                const outstanding = Number(p.net_outstanding) ?? (gross - adv);
                return (
                  <>
                    <tr key={p.id}>
                      <td style={{ fontSize: 12 }}>{fmtDate(p.purchase_date)}</td>
                      <td style={{ fontWeight: 500 }}>{p.supplier || '—'}</td>
                      <td style={{ fontSize: 12 }}>{p.project_name || '—'}</td>
                      <td>
                        {p.invoice_no
                          ? <span style={{ fontFamily: 'monospace', fontSize: 11, background: '#ebf8ff', color: '#2b6cb0', padding: '2px 6px', borderRadius: 6 }}>{p.invoice_no}</span>
                          : <span style={{ color: '#718096' }}>—</span>}
                        {p.invoice_image && (
                          <button style={{ marginLeft: 4, background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, padding: 0, verticalAlign: 'middle' }}
                            title="View invoice image"
                            onClick={() => builderApi.getInvoiceUrl(p.invoice_image).then(r => r?.url && window.open(r.url, '_blank'))}>
                            🖼️
                          </button>
                        )}
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 600, fontSize: 13 }}>{fmt(gross)}</td>
                      <td style={{ textAlign: 'right', fontSize: 12, color: adv > 0 ? '#38a169' : '#a0aec0' }}>
                        {adv > 0 ? fmt(adv) : '—'}
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 700,
                        color: outstanding > 0 ? '#e53e3e' : '#38a169', fontSize: 13 }}>
                        {fmt(outstanding)}
                      </td>
                      <td>
                        <button className="btn btn-outline" style={{ fontSize: 11, padding: '2px 7px' }}
                          onClick={() => setExpanded(expanded === p.id ? null : p.id)}>
                          {(p.items || []).length} {expanded === p.id ? '▲' : '▼'}
                        </button>
                      </td>
                      <td>
                        {isOwner && (
                          <button className="btn" style={{ background: '#fed7d7', color: '#c53030', padding: '3px 8px', fontSize: 12 }}
                            onClick={() => del(p.id)}>Del</button>
                        )}
                      </td>
                    </tr>
                    {expanded === p.id && (
                      <>
                        {(p.items || []).map((it, i) => (
                          <tr key={`${p.id}-${i}`} style={{ background: 'var(--bg-secondary)' }}>
                            <td colSpan={2} style={{ paddingLeft: 32, color: '#4a5568', fontSize: 13 }}>↳ {it.material_name}</td>
                            <td style={{ fontSize: 13 }}>{it.quantity} {it.unit}</td>
                            <td style={{ fontSize: 13 }}>{fmt(it.unit_price)}/unit</td>
                            <td colSpan={5} style={{ fontSize: 13 }}>{fmt(it.total_price)}</td>
                          </tr>
                        ))}
                        {Number(p.tax_pct) > 0 && (
                          <tr style={{ background: 'var(--bg-secondary)' }}>
                            <td colSpan={3} style={{ paddingLeft: 32, color: '#d69e2e', fontSize: 12, fontStyle: 'italic' }}>
                              Tax ({p.tax_pct}%)
                            </td>
                            <td colSpan={6} style={{ fontSize: 13, color: '#d69e2e', fontWeight: 600 }}>
                              + {fmt(p.tax_amount)}
                            </td>
                          </tr>
                        )}
                      </>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
